import React, {useCallback, useMemo} from "react";
import PropTypes from "prop-types";
import {useTranslation} from "react-i18next";
import NumberFormat from "react-number-format";
import {DataTable} from "../../components/table/data-table";
import CustomerCell from "../../components/common/table/customer-cell";
import {classNames} from "../../utils/Utils";
import {StatusBarIcon} from "../../components/common/alert/status-bar-icon";
import {getCustomFormat} from "../../utils/timeUtils";
import {useDocumentList} from "./hooks/useDocumentList";
import {fetchConnectionDocumentCollection} from "./services/document-service";
import DynamicFilterMenu from "../../components/common/table/dynamic-filter-menu";

const DEFAULT_HEIGHT = "h-[calc(100svh-140px)]";
const DEFAULT_FILTER_HEIGHT = "h-[calc(100svh-140px)]";

const resolveCustomer = (document) => (
    document?.customer
    ?? document?.company_document?.connection
    ?? null
);

const formatDateValue = (value) => (value ? getCustomFormat(value, "DD/MM/YYYY") : "-");

const collectTotalNodes = (document, sourceKey) => {
    if (!document || typeof document !== "object") {
        return [];
    }

    const pathSegments = Array.isArray(sourceKey)
        ? sourceKey
        : String(sourceKey ?? "")
            .split(".")
            .map((segment) => segment.trim())
            .filter(Boolean);

    let target = document;
    for (const segment of pathSegments) {
        if (!target || typeof target !== "object") {
            return [];
        }
        target = target?.[segment];
    }

    if (!target || typeof target !== "object") {
        return [];
    }

    const nodes = [];
    if (target?.general && typeof target.general === "object") {
        nodes.push(target.general);
    }
    nodes.push(target);

    return nodes;
};

const parseNumericValue = (value) => {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === "number" && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }
    return null;
};

const resolveTotalValue = (document, key, options = {}) => {
    const {source = null} = options;

    const sources = Array.isArray(source)
        ? source
        : source !== null && source !== undefined
            ? [source]
            : [
                "totals",
                "document_total",
                "documentTotal",
                "document_totals",
                "documentTotals",
            ];

    const visited = new Set();
    const candidates = [];

    sources.forEach((sourceKey) => {
        collectTotalNodes(document, sourceKey).forEach((node) => {
            if (node && typeof node === "object" && !visited.has(node)) {
                visited.add(node);
                candidates.push(node);
            }
        });
    });

    if (!source && document && typeof document === "object" && !visited.has(document)) {
        candidates.push(document);
    }

    for (const candidate of candidates) {
        const parsed = parseNumericValue(candidate?.[key]);
        if (parsed !== null) {
            return parsed;
        }
    }

    return parseNumericValue(document?.[key]);
};

const resolveDocumentTotal = (document) => {
    const candidates = [
        "total_discounted",
        "total_net_price",
        "total_unit_price",
        "total_purchase_price",
    ];

    for (const key of candidates) {
        const value = resolveTotalValue(document, key);
        if (value !== null) {
            return value;
        }
    }

    const fallback = parseNumericValue(document?.total);
    if (fallback !== null) {
        return fallback;
    }
    return 0;
};

const resolveCurrencyDisplay = (document) => {
    const currency = document?.currency ?? document?.currencies ?? null;
    if (!currency || typeof currency !== "object") {
        return {code: "", symbol: ""};
    }
    return {
        code: currency?.code ?? "",
        symbol: currency?.currency_symbol ?? currency?.symbol ?? "",
    };
};

const ConnectionDocumentList = ({type, title, onViewDocument}) => {
    const {t} = useTranslation();

    const {
        documents,
        totalRows,
        limit,
        offset,
        isLoading,
        isFetchingMore,
        handleFetchMore,
        handleSearch,
        handleSorting,
        availableSortColumns,
        dynamicFilters,
        defaultParams,
        activeDynamicFilter,
        handleDynamicFilterChange,
        lastCompanyType,
    } = useDocumentList({
        type,
        fetchCollection: fetchConnectionDocumentCollection,
    });

    const sortableColumns = useMemo(() => {
        if (Array.isArray(availableSortColumns) && availableSortColumns.length > 0) {
            return availableSortColumns.map((column) => String(column));
        }
        return [
            "customer_business_name",
            "internal_reference",
            "connection_note",
            "status",
            "registration_date",
            "delivery_date",
            "total",
        ];
    }, [availableSortColumns]);

    const canSortByKey = useCallback((sortKey) => {
        if (!sortKey) {
            return false;
        }
        return sortableColumns.includes(String(sortKey));
    }, [sortableColumns]);

    const handleRowClick = useCallback((documentId) => {
        if (typeof onViewDocument !== "function") {
            return;
        }
        const target = documents.find((item) => item?.id === documentId);
        if (target) {
            onViewDocument(target);
        }
    }, [documents, onViewDocument]);

    const {columns, visibilityDefaults} = useMemo(() => {
        const visibilityDefaults = {};
        const supplierHeader = t("app.common.supplier", {defaultValue: "Supplier"});

        const columns = [
            {
                id: "supplier",
                accessorFn: (row) => {
                    const supplier = resolveCustomer(row);
                    return supplier?.business_name ?? "-";
                },
                header: () => supplierHeader,
                title: supplierHeader,
                size: 320,
                meta: {sortKey: "customer_business_name"},
                enableHiding: false,
                enableSorting: canSortByKey("customer_business_name"),
                cell: ({row}) => {
                    const record = row.original;
                    const supplier = resolveCustomer(record);
                    const isInteractive = typeof onViewDocument === "function";

                    return (
                        <button
                            type="button"
                            onClick={() => {
                                if (isInteractive) {
                                    onViewDocument(record);
                                }
                            }}
                            className={classNames(
                                "flex w-full flex-col truncate text-left focus:outline-none",
                                isInteractive ? "cursor-pointer hover:text-gray-600" : "cursor-default"
                            )}
                        >
                            <CustomerCell customer={supplier}/>
                        </button>
                    );
                },
            },
            {
                id: "internal_reference",
                accessorFn: (row) => row?.internal_reference ?? "",
                header: () => t("app.documents.internal_reference", {defaultValue: "Internal reference"}),
                title: t("app.documents.internal_reference", {defaultValue: "Internal reference"}),
                meta: {sortKey: "internal_reference"},
                enableSorting: canSortByKey("internal_reference"),
                enableHiding: false,
                cell: ({row, getValue}) => {
                    const value = getValue() || "-";
                    const record = row.original;
                    const isInteractive = typeof onViewDocument === "function";

                    return (
                        <button
                            type="button"
                            onClick={() => {
                                if (isInteractive) {
                                    onViewDocument(record);
                                }
                            }}
                            className={classNames(
                                "text-sm text-left font-medium text-gray-900 focus:outline-none",
                                isInteractive ? "cursor-pointer hover:text-gray-600" : "cursor-default"
                            )}
                        >
                            {value}
                        </button>
                    );
                },
            },
            {
                id: "connection_note",
                accessorFn: (row) => row?.connection_note ?? "",
                header: () => t("app.quotations.customer_note", {defaultValue: "Customer note"}),
                title: t("app.quotations.customer_note", {defaultValue: "Customer note"}),
                enableSorting: canSortByKey("connection_note"),
                size: 260,
                cell: ({getValue}) => {
                    const note = getValue();
                    if (!note) {
                        return <span className="text-sm text-gray-500">-</span>;
                    }
                    return (
                        <span className="text-sm text-gray-900 whitespace-pre-line">
                            {note}
                        </span>
                    );
                },
            },
            {
                id: "status",
                accessorKey: "status",
                header: () => t("app.common.status", {defaultValue: "Status"}),
                title: t("app.common.status", {defaultValue: "Status"}),
                meta: {sortKey: "status"},
                enableSorting: canSortByKey("status"),
                cell: ({row}) => {
                    const status = (row.original?.status ?? "").toLowerCase();
                    return (
                        <div className="flex flex-col gap-1">
                            <StatusBarIcon status={status}/>
                        </div>
                    );
                },
            },
            {
                id: "registration_date",
                accessorFn: (row) => row?.registration_date ?? "",
                header: () => t("app.documents.registration_date", {defaultValue: "Registration date"}),
                title: t("app.documents.registration_date", {defaultValue: "Registration date"}),
                meta: {sortKey: "registration_date"},
                enableSorting: canSortByKey("registration_date"),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{formatDateValue(getValue())}</span>
                ),
            },
            {
                id: "delivery_date",
                accessorFn: (row) => row?.delivery_date ?? "",
                header: () => t("app.documents.delivery_date", {defaultValue: "Delivery date"}),
                title: t("app.documents.delivery_date", {defaultValue: "Delivery date"}),
                meta: {sortKey: "delivery_date"},
                enableSorting: canSortByKey("delivery_date"),
                cell: ({getValue}) => (
                    <span className="text-sm text-gray-900">{formatDateValue(getValue())}</span>
                ),
            },
            {
                id: "total",
                accessorFn: (row) => resolveTotalValue(row, "total_net_price") ?? resolveDocumentTotal(row),
                header: () => t("app.quotations.price", {defaultValue: "Price"}),
                title: t("app.quotations.price", {defaultValue: "Price"}),
                meta: {sortKey: "total"},
                enableSorting: canSortByKey("total"),
                cell: ({row, getValue}) => {
                    const totalValue = getValue();
                    const fallbackTotal = totalValue !== null ? totalValue : resolveDocumentTotal(row.original);
                    const {symbol, code} = resolveCurrencyDisplay(row.original);
                    const currencyToken = symbol || code || "";
                    const suffix = currencyToken ? ` ${currencyToken}` : "";

                    return (
                        <NumberFormat
                            value={fallbackTotal}
                            displayType="text"
                            thousandSeparator="."
                            decimalSeparator=","
                            decimalScale={2}
                            suffix={suffix}
                        />
                    );
                },
            },
        ];
        return {columns, visibilityDefaults};
    }, [canSortByKey, onViewDocument, t]);

    const tableTitle = title ?? t("app.quotations.supplier_quotations", {defaultValue: "Quotations"});

    return (
        <div className="shadow-sm">
            <DataTable
                data={documents}
                columns={columns}
                total={totalRows}
                offset={offset}
                limit={limit}
                isLoading={isLoading}
                isFetchingMore={isFetchingMore}
                onFetchingMore={handleFetchMore}
                onSearch={handleSearch}
                onSorting={handleSorting}
                leftPinning={["supplier"]}
                paginationMode="infinite"
                activePagination={false}
                fixedHeight={DEFAULT_HEIGHT}
                fixedHeightFilter={DEFAULT_FILTER_HEIGHT}
                title={tableTitle}
                totalRows={totalRows}
                onClickRow={handleRowClick}
                activeDynamicFilter={activeDynamicFilter}
                enableColumnVisibility
                enableColumnReorder
                initialColumnVisibility={visibilityDefaults}
                listKey={`connection-documents-${type}`}
            >
                <DynamicFilterMenu
                    filters={dynamicFilters || []}
                    defaultParams={defaultParams}
                    lastCompanyType={lastCompanyType}
                    isLoading={isLoading && documents.length === 0}
                    filterType={`${type}`}
                    onChange={handleDynamicFilterChange}
                />
            </DataTable>
        </div>
    );
};

ConnectionDocumentList.propTypes = {
    type: PropTypes.string.isRequired,
    title: PropTypes.string,
    onViewDocument: PropTypes.func,
};

ConnectionDocumentList.defaultProps = {
    title: undefined,
    onViewDocument: undefined,
};

export default ConnectionDocumentList;
